import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { DraftStatus, SendStatus } from '@mailpilot/shared';

export type Tone = 'neutral' | 'primary' | 'success' | 'warn' | 'danger' | 'info' | 'lime' | 'yellow' | 'lavender' | 'salmon' | 'sky' | 'mint';

/* Semantic tones map onto the vivid pill palette (dark ink on bright fills) so chips pop in both themes. */
const tones: Record<Tone, string> = {
  neutral: 'mp-pill-neutral',
  primary: 'mp-pill-primary',
  success: 'mp-pill-lime',
  warn: 'mp-pill-yellow',
  danger: 'mp-pill-salmon',
  info: 'mp-pill-sky',
  lime: 'mp-pill-lime',
  yellow: 'mp-pill-yellow',
  lavender: 'mp-pill-lavender',
  salmon: 'mp-pill-salmon',
  sky: 'mp-pill-sky',
  mint: 'mp-pill-mint',
};

export function Badge({ tone = 'neutral', children, icon, className, title }: { tone?: Tone; children: ReactNode; icon?: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={clsx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold leading-5', tones[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

export function CountPill({ n, active, tone = 'primary' }: { n: number; active?: boolean; tone?: 'primary' | 'danger' }) {
  if (!n) return null;
  return (
    <span
      className={clsx(
        'ml-auto min-w-[1.5rem] rounded-full px-1.5 text-center text-[0.6875rem] font-semibold leading-5',
        active ? 'bg-white/25 text-white' : tone === 'danger' ? 'mp-pill-salmon' : 'mp-pill-lavender',
      )}
    >
      {n > 999 ? '999+' : n}
    </span>
  );
}

/** Small coloured dot + label for a mailbox. */
export function AccountBadge({ color, label, className }: { color: string; label: string; className?: string }) {
  return (
    <span className={clsx('inline-flex max-w-[10rem] items-center gap-1.5 truncate rounded-full border border-line bg-surface px-2 py-0.5 text-[0.6875rem] font-medium text-muted', className)} title={label}>
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
}

export const draftStatusMeta: Record<DraftStatus, { label: string; tone: Tone; note?: string }> = {
  draft: { label: 'Drafted', tone: 'neutral' },
  pending_attachment: { label: 'Pending attachment', tone: 'warn', note: 'Waiting for you to choose or upload a requested file.' },
  pending_approval: { label: 'Pending approval', tone: 'warn', note: 'Needs your approval before it is sent.' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  queued: { label: 'Queued', tone: 'info', note: 'Queued — the server re-checks the thread before sending.' },
  sending: { label: 'Sending', tone: 'info' },
  provider_accepted: { label: 'Accepted by provider', tone: 'success', note: 'Accepted by provider — delivery not confirmed.' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  stale: { label: 'Stale', tone: 'warn', note: 'The thread changed after this draft was written (someone already replied).' },
};

export function DraftStatusChip({ status }: { status: DraftStatus }) {
  const m = draftStatusMeta[status];
  return (
    <Badge tone={m.tone} title={m.note}>
      {m.label}
    </Badge>
  );
}

export const sendStatusMeta: Record<SendStatus, { label: string; tone: Tone }> = {
  queued: { label: 'Queued', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  provider_accepted: { label: 'Accepted by provider', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  unknown: { label: 'Unknown — reconciling', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function SendStatusChip({ status }: { status: SendStatus }) {
  const m = sendStatusMeta[status];
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

export function ProgressBar({ value, max, label }: { value: number; max: number | null; label: string }) {
  const pct = max && max > 0 ? Math.min(100, Math.round((value / max) * 100)) : null;
  return (
    <div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max ?? undefined}
        aria-valuenow={pct === null ? undefined : value}
        className="h-2 w-full overflow-hidden rounded-full bg-sunken"
      >
        <div className={clsx('h-full rounded-full bg-primary transition-[width] duration-500', pct === null && 'w-1/3 opacity-60')} style={pct === null ? undefined : { width: `${pct}%` }} />
      </div>
    </div>
  );
}
